import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { listAvatarPresetsApi, listDepartmentsApi } from '@/lib/employees-api';
import {
  createDepartmentApi,
  deleteDepartmentApi,
  getHrConfigApi,
  updateDepartmentApi,
  updateHrConfigApi,
} from '@/lib/settings-api';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';
import { DeleteWithCaptchaModal } from '@/components/ui/delete-captcha';
import type { DeleteCaptchaProof } from '@/lib/captcha-api';
import { DEFAULT_HR_ROLE_OPTIONS, normalizeHrRoleOptions } from '@/lib/hr-config';
import type { HrRoleOption } from '@/lib/types';
import { NameListEditor } from './name-list-editor';
import jzLogo from '@/assets/jzlogo.png';

/**
 * Issue #184 — 人力资源设置：部门管理 / 岗位管理 / 默认头像 / 默认密码。
 *
 * 部门是真实 Department 实体（即时 CRUD）；岗位 / 角色显示名 / 默认密码 / 默认头像走
 * GET/PATCH /system/hr-config（按 orgId 隔离）。全部真实后端，刷新后保持。
 */
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const INPUT =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

function errMsg(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.status > 0 ? `${err.message}（HTTP ${err.status}）` : err.message;
  return err instanceof Error ? err.message : fallback;
}

export function HrSettingsCard({ canEdit }: { canEdit: boolean }) {
  return (
    <Card
      title="人力资源"
      description="部门管理、岗位管理、角色管理、默认头像、默认密码。新员工默认密码用于首次登录，登录后强制改密。"
    >
      <div className="flex flex-col gap-6">
        <DepartmentsSection canEdit={canEdit} />
        <HrConfigSection canEdit={canEdit} />
      </div>
    </Card>
  );
}

// ─── 部门管理（真实 Department CRUD） ───
function DepartmentsSection({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin', 'departments'], queryFn: listDepartmentsApi });
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'departments'] });

  const createM = useMutation({
    mutationFn: (name: string) => createDepartmentApi(name),
    onSuccess: async () => {
      setNewName('');
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(errMsg(err, '新增部门失败。')),
  });
  const renameM = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateDepartmentApi(id, name),
    onSuccess: async () => {
      setEditing(null);
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(errMsg(err, '重命名部门失败。')),
  });
  // Issue #235：部门删除改为图形验证码确认弹窗（替代 window.confirm）。
  const [deletingDept, setDeletingDept] = useState<{ id: string; name: string } | null>(null);

  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-slate-800">部门管理</h3>
      {query.isLoading ? (
        <LoadingState label="正在加载部门…" />
      ) : query.isError ? (
        <ErrorState
          title="加载部门失败"
          description={errMsg(query.error, '请稍后重试。')}
          retry={() => void query.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-1.5">
          {(query.data ?? []).map((d) => (
            <div key={d.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm">
              {editing?.id === d.id ? (
                <>
                  <input
                    className={`${INPUT} flex-1`}
                    value={editing.name}
                    onChange={(e) => setEditing({ id: d.id, name: e.target.value })}
                    autoFocus
                  />
                  <button
                    className={SECONDARY_BTN}
                    onClick={() => renameM.mutate({ id: d.id, name: editing.name })}
                    disabled={renameM.isPending}
                  >
                    <Check className="h-3.5 w-3.5" /> 保存
                  </button>
                  <button className={SECONDARY_BTN} onClick={() => setEditing(null)}>
                    <X className="h-3.5 w-3.5" /> 取消
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-slate-800">{d.name}</span>
                  {canEdit ? (
                    <>
                      <button className={SECONDARY_BTN} onClick={() => setEditing({ id: d.id, name: d.name })}>
                        <Pencil className="h-3.5 w-3.5" /> 重命名
                      </button>
                      <button
                        className={`${SECONDARY_BTN} text-rose-600 hover:bg-rose-50`}
                        onClick={() => {
                          setError(null);
                          setDeletingDept({ id: d.id, name: d.name });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> 删除
                      </button>
                    </>
                  ) : null}
                </>
              )}
            </div>
          ))}
          {(query.data ?? []).length === 0 ? (
            <span className="text-xs text-slate-400">暂无部门，请在下方新增。</span>
          ) : null}
        </div>
      )}

      {canEdit ? (
        <div className="mt-2 flex items-center gap-2">
          <input
            className={`${INPUT} flex-1`}
            value={newName}
            placeholder="新增部门名称（如 健管部）"
            maxLength={32}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) createM.mutate(newName.trim());
            }}
          />
          <button
            className={PRIMARY_BTN}
            onClick={() => createM.mutate(newName.trim())}
            disabled={createM.isPending || newName.trim().length < 2}
          >
            <Plus className="h-4 w-4" /> 新增部门
          </button>
        </div>
      ) : null}
      {error ? (
        <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
      ) : null}

      {deletingDept ? (
        <DeleteWithCaptchaModal
          key={`del-dept-${deletingDept.id}`}
          action="department:delete"
          title={`软删除部门 · ${deletingDept.name}`}
          description="在职员工的历史归属将保留；删除前需通过图形验证码防误删。"
          confirmLabel="确认软删除"
          onClose={() => setDeletingDept(null)}
          onConfirm={async (proof) => {
            await deleteDepartmentApi(deletingDept.id, proof);
            await invalidate();
          }}
        />
      ) : null}
    </section>
  );
}

// ─── 岗位 / 角色 / 默认密码 / 默认头像（hr-config，一次保存） ───
function HrConfigSection({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['system', 'hr-config'], queryFn: getHrConfigApi });
  const presetsQuery = useQuery({
    queryKey: ['admin', 'avatar-presets'],
    queryFn: listAvatarPresetsApi,
    staleTime: 60 * 60 * 1000,
  });

  const [positions, setPositions] = useState<string[] | null>(null);
  const [roles, setRoles] = useState<HrRoleOption[]>(DEFAULT_HR_ROLE_OPTIONS);
  const [defaultPassword, setDefaultPassword] = useState('');
  const [defaultAvatarUrl, setDefaultAvatarUrl] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Issue #235：删除岗位需先过图形验证码（position:delete）。
  const [showPositionCaptcha, setShowPositionCaptcha] = useState(false);

  useEffect(() => {
    if (query.data) {
      setPositions(query.data.positions);
      setRoles(normalizeHrRoleOptions(query.data.roles));
      setDefaultPassword(query.data.defaultPassword);
      setDefaultAvatarUrl(query.data.defaultAvatarUrl);
    }
  }, [query.data]);

  const mutation = useMutation({
    // Issue #235：仅当本次保存删除了已存在岗位时才携带 proof；后端据此强制校验。
    mutationFn: (proof?: DeleteCaptchaProof) =>
      updateHrConfigApi({
        positions: positions ?? [],
        roles,
        defaultPassword,
        defaultAvatarUrl,
        ...(proof ?? {}),
      }),
    onSuccess: async (data) => {
      setSaved(true);
      setError(null);
      setPositions(data.positions);
      setRoles(normalizeHrRoleOptions(data.roles));
      setDefaultPassword(data.defaultPassword);
      setDefaultAvatarUrl(data.defaultAvatarUrl);
      await queryClient.invalidateQueries({ queryKey: ['system', 'hr-config'] });
    },
    onError: (err) => setError(errMsg(err, '保存失败，请重试。')),
  });

  // 已持久化岗位（服务端真相）中被本次编辑移除的项 = 「岗位删除」，触发验证码。
  const persistedPositions = query.data?.positions ?? [];
  const removedPositions = persistedPositions.filter(
    (p) => !(positions ?? []).includes(p),
  );

  function handleSave() {
    setError(null);
    if (removedPositions.length > 0) {
      // 删除岗位 → 先弹验证码，确认后随 PATCH 携带 proof。
      setShowPositionCaptcha(true);
    } else {
      // 纯新增 / 重排 / 仅改密码或头像 → 无需验证码。
      mutation.mutate(undefined);
    }
  }

  if (query.isLoading || positions === null) {
    return <LoadingState label="正在加载人力资源配置…" />;
  }
  if (query.isError) {
    return (
      <ErrorState
        title="加载人力资源配置失败"
        description={errMsg(query.error, '请稍后重试。')}
        retry={() => void query.refetch()}
      />
    );
  }

  const presetUrls = presetsQuery.data?.urls ?? [];
  const factoryDefault = presetsQuery.data?.defaultUrl;

  return (
    <>
      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">岗位管理</h3>
        <NameListEditor
          items={positions}
          onChange={(next) => {
            setSaved(false);
            setPositions(next);
          }}
          disabled={!canEdit}
          placeholder="输入岗位名称后回车添加（如 销售顾问）"
          emptyHint="暂无岗位"
        />
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">角色管理</h3>
        <div className="flex flex-col gap-1.5">
          {roles.map((role) => (
            <div
              key={role.value}
              className="grid grid-cols-[96px_1fr] items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm"
            >
              <span className="text-xs font-mono text-slate-500">{role.value}</span>
              <input
                className={INPUT}
                value={role.label}
                maxLength={32}
                disabled={!canEdit}
                onChange={(e) => {
                  const label = e.target.value;
                  setSaved(false);
                  setRoles((prev) =>
                    prev.map((item) =>
                      item.value === role.value ? { ...item, label } : item,
                    ),
                  );
                }}
              />
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          角色权限值固定为 STAFF / MANAGER / ADMIN；这里只调整员工表单和列表中的中文显示名。
        </p>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">默认头像</h3>
        <div className="flex flex-wrap items-center gap-2">
          {factoryDefault ? (
            <button
              type="button"
              className={`h-10 w-10 overflow-hidden rounded-full border ${defaultAvatarUrl === factoryDefault ? 'border-brand-500 ring-2 ring-brand-100' : 'border-slate-200'}`}
              title="金专 logo（出厂默认）"
              onClick={() => {
                if (!canEdit) return;
                setSaved(false);
                setDefaultAvatarUrl(factoryDefault);
              }}
            >
              <DefaultAvatarThumb src={factoryDefault} />
            </button>
          ) : null}
          {presetUrls.map((url) => (
            <button
              key={url}
              type="button"
              className={`h-10 w-10 overflow-hidden rounded-full border ${defaultAvatarUrl === url ? 'border-brand-500 ring-2 ring-brand-100' : 'border-slate-200'}`}
              onClick={() => {
                if (!canEdit) return;
                setSaved(false);
                setDefaultAvatarUrl(url);
              }}
            >
              <DefaultAvatarThumb src={url} />
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          新建员工（未单独选头像）与未上传真人头像的员工，都将显示这里设置的默认头像（未设置时为金专 logo）。
        </p>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">默认密码</h3>
        <input
          className={`${INPUT} w-48 font-mono`}
          value={defaultPassword}
          maxLength={64}
          disabled={!canEdit}
          onChange={(e) => {
            setSaved(false);
            setDefaultPassword(e.target.value);
          }}
        />
        <p className="mt-1.5 text-xs text-slate-400">
          6-64 位。导入 / 重置员工时使用；员工首次登录强制改密。
        </p>
      </section>

      {canEdit ? (
        <div className="flex items-center gap-3">
          <button className={PRIMARY_BTN} onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? '保存中…' : '保存人力资源配置'}
          </button>
          {removedPositions.length > 0 ? (
            <span className="text-xs text-amber-600">
              将删除岗位：{removedPositions.join('、')}（保存需验证码）
            </span>
          ) : saved ? (
            <span className="text-xs text-emerald-600">已保存，刷新后保持。</span>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-slate-500">仅 ADMIN 及以上角色可修改人力资源配置。</p>
      )}
      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
      ) : null}

      {showPositionCaptcha ? (
        <DeleteWithCaptchaModal
          action="position:delete"
          title="删除岗位需验证"
          description="本次保存将删除已存在岗位，请通过图形验证码确认（防误删）。新增 / 重排不受影响。"
          body={
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              将删除岗位：{removedPositions.join('、')}
            </div>
          }
          confirmLabel="确认删除并保存"
          onClose={() => {
            setShowPositionCaptcha(false);
            setError(null);
          }}
          onConfirm={async (proof) => {
            await mutation.mutateAsync(proof);
            setShowPositionCaptcha(false);
          }}
        />
      ) : null}
    </>
  );
}

function DefaultAvatarThumb({ src }: { src: string }) {
  const [broken, setBroken] = useState(false);
  return (
    <img
      src={broken ? jzLogo : src}
      alt="头像"
      className="h-full w-full object-cover"
      onError={() => setBroken(true)}
    />
  );
}
